import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { TiltCard } from "@/components/common/TiltCard";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { SkeletonRows } from "@/components/common/Skeleton";
import { SeverityBadge, StatusBadge } from "@/components/common/Badges";
import { FindingDetailSheet } from "@/components/common/FindingDetailSheet";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TID } from "@/lib/testIds";

export default function Findings() {
    // Dashboard drill-downs land here with pre-applied filters
    // (e.g. /findings?severity=critical&engine=authorization).
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(null);
    const [severity, setSeverity] = useState(searchParams.get("severity") || "all");
    const [engine, setEngine] = useState(searchParams.get("engine") || "all");
    const [status, setStatus] = useState(searchParams.get("status") || "all");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const params = {};
        if (severity !== "all") params.severity = severity;
        if (engine !== "all") params.engine = engine;
        if (status !== "all") params.status = status;
        setLoading(true);
        try {
            const { data } = await api.get("/findings", { params });
            setItems(data);
        } catch (e) {
            console.warn("Failed to load findings", e);
        } finally {
            setLoading(false);
        }
    }, [severity, engine, status]);
    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => {
        const c = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
        items.forEach((f) => { c[f.severity] = (c[f.severity] || 0) + 1; });
        return c;
    }, [items]);

    return (
        <div className="space-y-6">
            <motion.div
                variants={staggerContainer}
                initial="initial"
                animate="animate"
                className="grid grid-cols-2 md:grid-cols-5 gap-3"
            >
                {["critical", "high", "medium", "low", "info"].map((s) => (
                    <motion.div key={s} variants={staggerItem}>
                        <TiltCard
                            max={6}
                            testId={`finding-severity-tile-${s}`}
                            onClick={() => { setOpen(null); setSeverity(severity === s ? "all" : s); }}
                            className={`group panel-elevated card-lift px-4 py-3 rounded-[4px] cursor-pointer transition-colors ${
                                severity === s ? "border-cyan-400/50" : ""
                            }`}
                        >
                            <div style={{ transform: "translateZ(24px)" }}>
                                <SeverityBadge severity={s} />
                            </div>
                            <div className="font-display text-3xl text-white mt-2" style={{ transform: "translateZ(36px)" }}>
                                <AnimatedNumber value={counts[s] || 0} />
                            </div>
                        </TiltCard>
                    </motion.div>
                ))}
            </motion.div>

            <Panel
                eyebrow="Filters"
                title="Engineering Findings"
                action={
                    <div className="flex items-center gap-2">
                        <Select value={severity} onValueChange={setSeverity}>
                            <SelectTrigger data-testid={TID.findingFilterSeverity} className="w-[140px] h-8 bg-[var(--bg-surface-2)] border-white/10 text-xs">
                                <SelectValue placeholder="Severity" />
                            </SelectTrigger>
                            <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                <SelectItem value="all">All severities</SelectItem>
                                <SelectItem value="critical">Critical</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="info">Info</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={engine} onValueChange={setEngine}>
                            <SelectTrigger data-testid={TID.findingFilterEngine} className="w-[140px] h-8 bg-[var(--bg-surface-2)] border-white/10 text-xs">
                                <SelectValue placeholder="Engine" />
                            </SelectTrigger>
                            <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                <SelectItem value="all">All engines</SelectItem>
                                <SelectItem value="authorization">Authorization</SelectItem>
                                <SelectItem value="su24">SU24</SelectItem>
                                <SelectItem value="odata">OData</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-[140px] h-8 bg-[var(--bg-surface-2)] border-white/10 text-xs">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                }
            >
                <table className="tbl">
                    <thead>
                        <tr>
                            <th>Severity</th>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Engine</th>
                            <th>Confidence</th>
                            <th>Status</th>
                            <th>Detected</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="p-0"><SkeletonRows rows={6} cols={7} /></td></tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-xs text-slate-500 font-mono py-8">No findings match the current filter.</td></tr>
                        )}
                        {!loading && items.map((f) => (
                            <tr
                                key={f.id}
                                data-testid={TID.findingRow(f.id)}
                                onClick={() => setOpen(f)}
                                className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                            >
                                <td><SeverityBadge severity={f.severity} /></td>
                                <td className="max-w-[400px]">{f.title}</td>
                                <td className="text-slate-400">{f.category}</td>
                                <td className="font-mono text-[11px] uppercase text-cyan-300/80">{f.engine}</td>
                                <td className="font-mono text-cyan-300">{Math.round((f.confidence || 0) * 100)}%</td>
                                <td><StatusBadge status={f.status} /></td>
                                <td className="font-mono text-[11px] text-slate-500">{new Date(f.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Panel>

            <FindingDetailSheet finding={open} onClose={() => setOpen(null)} />
        </div>
    );
}
